import Tag from './Tag'
import ID from './ID'
import PlanDocument from './PlanDocument'
import isValidDate from '@/utils/isValidDate'
import { parseAllDayDate } from '@/utils/parseAllDayDate'
import InitiativeType from './InitiativeType'
import InitiativeCustomization from './InitiativeCustomization'
import Channel from './Channel'
import PlanningItem, { InitiativeSummaryData } from './InterfacePlanningItem'

export default class Initiative implements PlanningItem {
  public editing = false
  public opened = false
  public newTitle: string
  public newStartDate: Date
  public newEndDate: Date
  public newFile: any
  public newFileName: string
  public newSummary: string
  public initiativeSummary: InitiativeSummaryData | null = null

  constructor(
    public id: ID = new ID(),
    public creatorId: ID = new ID(),
    public title: string = '',
    public startDate: Date = new Date(Date.now()),
    public endDate: Date = new Date(Date.now()),
    public initiativeType: InitiativeType = new InitiativeType(),
    public tagIds: ID[] = [],
    public isFlagged: boolean = false,
    public planId: ID = new ID(),
    public isAllDay: boolean = false,
    public isNested: boolean = false,
    public isLead: boolean = false,
    public documentIds: ID[] = [],
    public summary: string = '',
    public isUnscheduled: boolean = false,
    public initiativeCustomizations: InitiativeCustomization[] = [],

    public initiativeTypeName: string = '', // Populated on front-end
    public abbreviatedPlanName: string = '', // Populated on front-end
    public tags: Tag[] = [], // Populated on front-end
    public documents: PlanDocument[] = [], // Populated on front-end
  ) {
    this.newTitle = title
    this.newStartDate = startDate
    this.newEndDate = endDate
    this.newFile = null
    this.newFileName = ''
    this.newSummary = summary
  }

  public get key() {
    return 'initiative-' + this.id.intID.toString()
  }

  public get channelName(): string {
    return Channel.CHANNEL_NAME_INITIATIVES
  }

  public get startDateString(): string {
    return this.startDate.toLocaleDateString()
  }

  public get endDateString(): string {
    return this.endDate.toLocaleDateString()
  }

  public get start(): Date {
    return this.startDate
  }

  public get end(): Date {
    if (this.isAllDay) {
      // Add one day to end date of all day events to resolve confusing issue (https://github.com/fullcalendar/fullcalendar/issues/2909)
      const modifiedDate = new Date(this.endDate.getTime())
      modifiedDate.setDate(modifiedDate.getDate() + 1)
      return modifiedDate
    }
    return this.endDate
  }

  public get allDay(): boolean {
    return this.isAllDay
  }

  public get isInitiative(): boolean {
    return true
  }

  // TODO: remove setter if possible
  public set isInitiative(value: boolean) {
    // swallow value
  }

  public get duration(): number | object {
    const datesDifference = this.endDate.getTime() - this.startDate.getTime()
    if(datesDifference === 0){
      // Return one day if start and end date are equal
      if(this.isAllDay){
        return 86400000 // full day
      }
      return 60000 // one hour
    }
    if(this.isAllDay){
      // Add one day to the difference for all day tactics to work around exact 24-hour difference equaling one day
      return {
        days: Math.ceil(datesDifference / 86400000) + 1
      }
    }
    return datesDifference
  }

  public static fromResponseObject(obj): Initiative {
    if (!obj) return new Initiative()

    let startDateString = obj.startDate
    if (obj.startDate && obj.startDate.date) {
      startDateString = obj.startDate.date
    }

    let endDateString = obj.endDate
    if (obj.endDate && obj.endDate.date) {
      endDateString = obj.endDate.date
    }

    const tagIds = [] as ID[]
    if (obj.tags) {
      obj.tags.forEach((element) => {
        tagIds.push(ID.fromResponseObject(element, 'tags'))
      })
    }

    const documentIds = [] as ID[]
    if (obj.documents) {
      obj.documents.forEach((element) => {
        documentIds.push(ID.fromResponseObject(element, 'documents'))
      })
    }

    const initiativeType = InitiativeType.fromResponseObject(obj.initiativeType)
    const planId = ID.fromResponseObject(obj.plan, 'plans')

    const initiative = new Initiative(
      ID.fromResponseObject(obj.id, 'initiatives'),
      ID.fromResponseObject(obj.creator, 'users'),
      obj.title,
      obj.isAllDay ? parseAllDayDate(startDateString) : new Date(startDateString),
      obj.isAllDay ? parseAllDayDate(endDateString) : new Date(endDateString),
      initiativeType,
      tagIds,
      obj.isFlagged,
      planId,
      obj.isAllDay,
      obj.isNested ? obj.isNested : false,
      obj.isLead ? obj.isLead : false,
      documentIds,
      '',
      obj.isUnscheduled,
      obj.initiativeCustomizations ? obj.initiativeCustomizations.map((customization) => InitiativeCustomization.fromResponseObject(customization)) : []
    )
    initiative.initiativeSummary = obj.initiativeSummary ? { id: ID.fromResponseObject(obj.initiativeSummary['@id'], 'initiative_summaries'), contentUrl: obj.initiativeSummary.contentUrl } : null
    return initiative
  }

  public forRequestObject() {
    return {
      id: this.id.intID == 0 ? null : this.id.apiID,
      creator: this.creatorId.intID == 0 ? null : this.creatorId.apiID,
      title: this.title,
      startDate: isValidDate(this.startDate) ? this.startDate : new Date(Date.now()),
      endDate: isValidDate(this.endDate) ? this.endDate : new Date(Date.now()),
      initiativeType: this.initiativeType.id.intID == 0 ? null : this.initiativeType.id.apiID,
      tags: this.tags.map((tag) => tag.id.apiID),
      isFlagged: this.isFlagged,
      plan: this.planId.intID == 0 ? null : this.planId.apiID,
      isAllDay: this.isAllDay,
      documents: this.documents.map((document) => document.id.apiID),
      isUnscheduled: this.isUnscheduled,
    }
  }
}
